// tslint:disable:jsx-boolean-value
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import ObjectLink from 'components/ui/ObjectLink';
import PageInfo, { PageInfoProps } from './PageInfo';
import { isEditMode, isMaestroStreamMode, isStreamMethodSelectMode } from 'services/admin/selectors';
import { setAdminMode, setActiveAction, setOfflineSettingsTimestamp, setHomePage } from 'services/admin/actions';
import {
  ADMIN_ACCENT_PRIMARY,
  ADMIN_SURFACE_2,
  ADMIN_SURFACE_6,
} from 'style/constants';
import { usePageNavigationContext } from './Context';
import { PageType } from 'models';
import QuickActionsMenu from 'components/admin2/ui/QuickActionMenu';
import { clonePage } from 'services/page';
import { useAdminTranslation } from 'hooks/use-translation';

export interface PageMatchProps extends PageInfoProps {
  isCurrentPage?: boolean;
  pageName: string;
  pageSlug: string;
  slug: string;
  type: PageType;
}

export default function({
  isCurrentPage,
  pageId,
  slug,
  name,
  pageName,
  pageSlug,
  type,
  hasChanges,
  isHome,
  className,
  image,
  tooltipPosition,
}: PageMatchProps) {
  const { closeNavigation } = usePageNavigationContext();
  const { t } = useAdminTranslation();
  const isStreamMode = useSelector(isMaestroStreamMode);
  const isStreamMethodSelect = useSelector(isStreamMethodSelectMode);
  const isEditing = useSelector(isEditMode);
  const dispatch = useDispatch();

  const onNavigation = () => {
    const shouldShowStreamMode = isStreamMethodSelect && type === PageType.CHANNEL;
    if (isStreamMode || isEditing || !shouldShowStreamMode) {
      dispatch(setActiveAction(null));
      dispatch(setAdminMode(null));
    }
    dispatch(setOfflineSettingsTimestamp(null));
    closeNavigation();
  };

  const changeHomePage = React.useCallback(() => {
    dispatch(setHomePage({ pageId }));
  }, [dispatch, pageId]);

  const duplicatePage = React.useCallback(() => {
    dispatch(clonePage(
      pageId,
      t('LABEL_CLONED_CHANNEL_DEFAULT_NAME', { originalName: pageName || name }),
      t('LABEL_CLONED_CHANNEL_DEFAULT_SLUG', { originalSlug: pageSlug || slug }),
      type,
    ));
    closeNavigation();
  }, [dispatch, pageId, pageName, pageSlug, type, slug, t, name, closeNavigation]);

  return (
    <PageMatch
      isCurrentPage={!!isCurrentPage}
      data-testid="editButton"
      onClick={onNavigation}
    >
      <ObjectLink collection="pages" plain slug={slug}/>
      <PageInfo
        type={type}
        hasChanges={hasChanges}
        isHome={isHome}
        className={className}
        image={image}
        tooltipPosition={tooltipPosition}
        pageId={pageId}
        name={name}
      />
      <QuickActionsMenu
        admin={true}
        actions={[
          {
            stringKey: 'ADMIN_ACTION_SET_AS_HOME',
            onClick: changeHomePage,
          },
          {
            stringKey: 'ADMIN_LABEL_DUPLICATE',
            onClick: duplicatePage,
          },
        ]}
        testId="channelOptionsButton"
      />
    </PageMatch>
  );
}

const PageMatch = styled.div<{ isCurrentPage: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 10px;
  position: relative;
  height: 46px;
  border-radius: 4.5px;
  background: ${ADMIN_SURFACE_2};
  ${({ isCurrentPage }) => isCurrentPage && css`
    &:after {
      content: "";
      position: absolute;
      left: 0;
      border-radius: 4.5px;
      width: calc(100% - 2px);
      height: calc(100% - 2px);
      border: 1px solid ${ADMIN_ACCENT_PRIMARY};
      pointer-events: none;
    }
  `}
  ${({ isCurrentPage }) => !isCurrentPage && css`
    &:hover {
      border: 1px solid ${ADMIN_SURFACE_6};
    }
  `};
  & > *:first-child {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
  }
`;
